import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import useHeartbeat from '../hooks/useHeartbeat'
import Button from '../components/ui/Button'
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import ThemeToggle from '../components/ui/ThemeToggle'
import UserPreferences from '../components/ui/UserPreferences'
import SupportEmailCTA from '../components/ui/SupportEmailCTA'
import ZennaStatsBar from '../components/ZennaStatsBar'
import OnboardingModal from '../components/OnboardingModal'
import WhatsNewModal from '../components/WhatsNewModal'
import FoundingMemberPopup from '../components/FoundingMemberPopup'
import CraftMenu from '../components/ui/CraftMenu'

const API_URL = import.meta.env.VITE_API_URL || ''

const Dashboard = () => {
  const navigate = useNavigate()
  const { user, logout, loading } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [showPreferences, setShowPreferences] = useState(false)
  const [userId, setUserId] = useState(null)
  
  const [folders, setFolders] = useState([])
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [showNewFolderInput, setShowNewFolderInput] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [editingFolderId, setEditingFolderId] = useState(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  
  const [projectSearch, setProjectSearch] = useState('')
  const [projectSort, setProjectSort] = useState('updated') // 'updated', 'created', 'name'
  const [showAllProjects, setShowAllProjects] = useState(false)
  const [selectedProjectIds, setSelectedProjectIds] = useState([])
  const [bulkMode, setBulkMode] = useState(false)

  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackSending, setFeedbackSending] = useState(false)
  const [showDonate, setShowDonate] = useState(false)
  
  // Founding Member popup state
  const [showFoundingPopup, setShowFoundingPopup] = useState(false)

  // What's New modal (re-trigger)
  const [showWhatsNew, setShowWhatsNew] = useState(false)

  // Craft browser modal
  const [showCraftMenu, setShowCraftMenu] = useState(false)

  // Active challenge banner
  const [activeChallenge, setActiveChallenge] = useState(null)
  useEffect(() => {
    fetch(`${API_URL}/community/challenges/active`)
      .then(r => r.json())
      .then(data => setActiveChallenge(data.challenge))
      .catch(() => {})
  }, [])

  // Referral program
  const [referralInfo, setReferralInfo] = useState(null)
  const [referralCopied, setReferralCopied] = useState(false)
  const [referralBonusMessage, setReferralBonusMessage] = useState(null)

  useEffect(() => {
    if (!user?.email) return
    // Redeem any pending referral code first
    let storedCode = null
    try {
      storedCode = localStorage.getItem('zenna_referral_code')
    } catch (e) { /* ignore */ }

    if (storedCode) {
      fetch(`${API_URL}/credits/referral/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, code: storedCode })
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setReferralBonusMessage(data.message)
            try { localStorage.removeItem('zenna_referral_code') } catch (e) { /* ignore */ }
            // No credit reload needed — bonus is granted on first paid upgrade
          } else if (data.already_redeemed) {
            try { localStorage.removeItem('zenna_referral_code') } catch (e) { /* ignore */ }
          }
        })
        .catch(() => {})
    }

    // Fetch this user's own referral info
    fetch(`${API_URL}/credits/referral?email=${encodeURIComponent(user.email)}`)
      .then(r => r.json())
      .then(data => { if (data.code) setReferralInfo(data) })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  const copyReferralLink = () => {
    if (!referralInfo) return
    navigator.clipboard.writeText(referralInfo.share_url).then(() => {
      setReferralCopied(true)
      setTimeout(() => setReferralCopied(false), 2000)
    })
  }

  // Onboarding modal — show on first visit
  const [showOnboarding, setShowOnboarding] = useState(false)
  useEffect(() => {
    try {
      const done = localStorage.getItem('zenna_onboarding_complete')
      if (!done && user?.email) {
        // Small delay so dashboard renders first
        const timer = setTimeout(() => setShowOnboarding(true), 600)
        return () => clearTimeout(timer)
      }
    } catch (e) { /* ignore */ }
  }, [user?.email])

  // Heartbeat tracking
  useHeartbeat(user?.email, 'dashboard')

  // Real credit tracking from database
  const [userCredits, setUserCredits] = useState({
    credits: 7,
    credits_used_this_month: 0,
    plan: 'free',
    limits: { generations: 7, exports: 50 }
  })

  useEffect(() => {
    if (user?.email) {
      fetchProjects()
      fetchUserId()
      fetchFolders()
      fetchUserCredits()
      checkFoundingEligibility()
    }
  }, [user])

  // Check if user is eligible for Founding Member
  const checkFoundingEligibility = async () => {
    if (!user?.email) return
    
    try {
      const res = await fetch(`${API_URL}/founding/check-eligibility/${encodeURIComponent(user.email)}`)
      const data = await res.json()
      
      if (data.eligible && data.showPopup) {
        // Small delay so dashboard loads first
        setTimeout(() => setShowFoundingPopup(true), 500)
      }
    } catch (err) {
      console.error('Failed to check founding eligibility:', err)
    }
  }

  const fetchUserCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/credits?email=${encodeURIComponent(user.email)}`)
      if (response.ok) {
        const data = await response.json()
        setUserCredits(data)
      }
    } catch (error) {
      console.error('Error fetching credits:', error)
    }
  }

  const fetchUserId = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/user?email=${encodeURIComponent(user.email)}`)
      if (response.ok) {
        const data = await response.json()
        setUserId(data.user?.id)
      }
    } catch (error) {
      console.error('Error fetching user ID:', error)
    }
  }

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true)
      const response = await fetch(`${API_URL}/projects?email=${encodeURIComponent(user.email)}`)
      if (response.ok) {
        const data = await response.json()
        setProjects(data.projects || [])
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }

  const fetchFolders = async () => {
    try {
      const response = await fetch(`${API_URL}/folders?email=${encodeURIComponent(user.email)}`)
      if (response.ok) {
        const data = await response.json()
        setFolders(data.folders || [])
      }
    } catch (error) {
      console.error('Error fetching folders:', error)
    }
  }

  const createFolder = async () => {
    if (!newFolderName.trim()) return
    try {
      const response = await fetch(`${API_URL}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, name: newFolderName.trim() })
      })
      if (response.ok) {
        const data = await response.json()
        setFolders(prev => [...prev, data.folder])
        setNewFolderName('')
        setShowNewFolderInput(false)
      }
    } catch (error) {
      console.error('Error creating folder:', error)
    }
  }

  const renameFolder = async (folderId) => {
    if (!editingFolderName.trim()) return
    try {
      const response = await fetch(`${API_URL}/folders/${folderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, name: editingFolderName.trim() })
      })
      if (response.ok) {
        setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: editingFolderName.trim() } : f))
        setEditingFolderId(null)
        setEditingFolderName('')
      }
    } catch (error) {
      console.error('Error renaming folder:', error)
    }
  }

  const deleteFolder = async (folderId) => {
    if (!confirm('Delete this folder? Projects inside will be moved to Unfiled.')) return
    try {
      const response = await fetch(`${API_URL}/folders/${folderId}?email=${encodeURIComponent(user.email)}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        setFolders(prev => prev.filter(f => f.id !== folderId))
        setProjects(prev => prev.map(p => p.folder_id === folderId ? { ...p, folder_id: null } : p))
        if (selectedFolder === folderId) setSelectedFolder(null)
      }
    } catch (error) {
      console.error('Error deleting folder:', error)
    }
  }

  const moveProjectToFolder = async (projectId, folderId) => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, folderId })
      })
      if (response.ok) {
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, folder_id: folderId } : p))
      }
    } catch (error) {
      console.error('Error moving project:', error)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation()
    if (!confirm('Are you sure you want to delete this project?')) return
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}?email=${encodeURIComponent(user.email)}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        setProjects(prev => prev.filter(p => p.id !== projectId))
      }
    } catch (error) {
      console.error('Error deleting project:', error)
    }
  }

  const handleDuplicateProject = async (projectId, e) => {
    if (e) e.stopPropagation()
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      })
      if (response.ok) {
        const data = await response.json()
        setProjects(prev => [data.project, ...prev])
      }
    } catch (error) {
      console.error('Error duplicating project:', error)
    }
  }

  const toggleProjectSelected = (projectId) => {
    setSelectedProjectIds(prev =>
      prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
    )
  }

  const handleBulkDelete = async () => {
    if (selectedProjectIds.length === 0) return
    if (!confirm(`Delete ${selectedProjectIds.length} project${selectedProjectIds.length > 1 ? 's' : ''}? This cannot be undone.`)) return
    try {
      const response = await fetch(`${API_URL}/projects/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, projectIds: selectedProjectIds })
      })
      if (response.ok) {
        setProjects(prev => prev.filter(p => !selectedProjectIds.includes(p.id)))
        setSelectedProjectIds([])
        setBulkMode(false)
      }
    } catch (error) {
      console.error('Bulk delete error:', error)
    }
  }

  const handleBulkMove = async (folderId) => {
    if (selectedProjectIds.length === 0) return
    try {
      const response = await fetch(`${API_URL}/projects/bulk-move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, projectIds: selectedProjectIds, folderId })
      })
      if (response.ok) {
        setProjects(prev => prev.map(p =>
          selectedProjectIds.includes(p.id) ? { ...p, folder_id: folderId || null } : p
        ))
        setSelectedProjectIds([])
        setBulkMode(false)
      }
    } catch (error) {
      console.error('Bulk move error:', error)
    }
  }

  const sendFeedback = async () => {
    if (!feedbackText.trim()) return
    setFeedbackSending(true)
    try {
      await fetch(`${API_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, feedback: feedbackText.trim(), timestamp: new Date().toISOString() })
      })
      alert('Thank you for your feedback! 💜')
      setFeedbackText('')
      setShowFeedback(false)
    } catch (error) {
      console.error('Error sending feedback:', error)
      alert('Failed to send feedback. Please try again.')
    } finally {
      setFeedbackSending(false)
    }
  }

  const filteredProjects = (() => {
    let result = selectedFolder === null
      ? projects.filter(p => !p.folder_id)
      : selectedFolder === 'all'
      ? projects
      : projects.filter(p => p.folder_id === selectedFolder)

    // Search filter
    if (projectSearch.trim()) {
      const q = projectSearch.toLowerCase()
      result = result.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.type || '').toLowerCase().includes(q)
      )
    }

    // Sort
    if (projectSort === 'name') {
      result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    } else if (projectSort === 'created') {
      result = [...result].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    } else {
      result = [...result].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    }

    return result
  })()

  // REAL stats from database
  const stats = {
    credits: {
      remaining: userCredits.credits,
      used: userCredits.credits_used_this_month,
      limit: userCredits.limits?.generations || 7
    },
    exports: { used: 0, total: userCredits.limits?.exports || 50 },
    projects: projects.length,
    thisMonth: projects.filter(p => {
      const created = new Date(p.created_at)
      const now = new Date()
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear()
    }).length
  }

  // Analytics — computed from project history
  const analytics = (() => {
    if (projects.length === 0) return null
    // Craft type frequency — use canvas_data.craftType (returned as craft_type),
    // fall back to project.type, skip 'chat'/'general' as non-craft placeholders
    const typeCounts = {}
    for (const p of projects) {
      const t = (p.craft_type || '').toLowerCase() || (p.type || '').toLowerCase()
      if (!t || t === 'chat' || t === 'general') continue
      typeCounts[t] = (typeCounts[t] || 0) + 1
    }
    const sorted = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])
    const favoriteCraft = sorted[0]?.[0] || null

    // Streak: days in the last 7 where user created or updated a project
    const now = new Date()
    const last7 = new Set()
    for (const p of projects) {
      const updated = new Date(p.updated_at)
      const diff = Math.floor((now - updated) / 86400000)
      if (diff >= 0 && diff < 7) {
        last7.add(diff)
      }
    }
    const weekDays = last7.size

    // Total designs — count images across all projects, or fall back to project count
    const imageCount = projects.reduce((sum, p) => sum + (Array.isArray(p.images) ? p.images.length : 0), 0)
    const totalDesigns = imageCount > 0 ? imageCount : projects.length

    // Days since first project
    const firstProject = projects.reduce((earliest, p) => {
      const d = new Date(p.created_at)
      return d < earliest ? d : earliest
    }, new Date())
    const daysUsing = Math.max(1, Math.floor((now - firstProject) / 86400000) + 1)

    return { favoriteCraft, weekDays, totalDesigns, daysUsing, typeCounts: sorted.slice(0, 3) }
  })()

  // Plan display names
  const planNames = {
    free: 'Free',
    hobby: 'Hobby',
    crafter: 'Crafter',
    creator: 'Creator',
    pro: 'Pro',
    founding: '👑 Founding Member'
  }

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  const getProjectType = (project) => {
    if (project.type && project.type !== 'general') return project.type
    if (project.images?.length > 0) return 'img'
    return 'new'
  }

  return (
    <div className="min-h-screen bg-zenna-cream dark:bg-slate-900 flex flex-col">
      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
      {!showOnboarding && <WhatsNewModal forceOpen={showWhatsNew} onClose={() => setShowWhatsNew(false)} />}
      <CraftMenu
        isOpen={showCraftMenu}
        onClose={() => setShowCraftMenu(false)}
        onSelect={(craft) => navigate(`/workspace?startCraft=${encodeURIComponent(craft.key)}`)}
      />

      {referralBonusMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-pulse">
          <span className="text-xl">🎉</span>
          <span className="text-sm font-medium">{referralBonusMessage}</span>
          <button onClick={() => setReferralBonusMessage(null)} className="text-white/80 hover:text-white ml-2">✕</button>
        </div>
      )}
      {/* Founding Member Popup */}
      {showFoundingPopup && (
        <FoundingMemberPopup
          userEmail={user.email}
          onClose={() => setShowFoundingPopup(false)}
          onClaimed={(data) => {
            console.log('Founding member claimed!', data)
            fetchUserCredits() // Refresh credits after claiming
          }}
        />
      )}

      {showPreferences && userId && (
        <UserPreferences userId={userId} userEmail={user?.email} onClose={() => setShowPreferences(false)} />
      )}

      {showFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white">💬 Send Feedback</h3>
              <button onClick={() => setShowFeedback(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Found a bug? Have a feature request? We'd love to hear from you!</p>
            <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} placeholder="Tell us what's on your mind..." className="w-full h-32 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50 text-sm resize-none" />
            <div className="flex gap-3 mt-4">
              <Button variant="secondary" className="flex-1" onClick={() => setShowFeedback(false)}>Cancel</Button>
              <Button className="flex-1" onClick={sendFeedback} disabled={!feedbackText.trim() || feedbackSending}>{feedbackSending ? 'Sending...' : 'Send Feedback'}</Button>
            </div>
          </div>
        </div>
      )}

      {showDonate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white">💜 Support Zenna's Development</h3>
              <button onClick={() => setShowDonate(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Zenna is built with love by a solo developer. Your support helps keep the servers running and new features coming!</p>
            <div className="space-y-3">
              <a href="https://www.paypal.com/paypalme/starrcustomclothing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#003087] flex items-center justify-center"><span className="text-white font-bold text-sm">PP</span></div>
                <div className="flex-1"><p className="font-medium text-slate-900 dark:text-white">PayPal</p><p className="text-xs text-slate-500">starrcustomclothing@gmail.com</p></div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
              <a href="https://cash.app/$Starrsie737" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#00D632] flex items-center justify-center"><span className="text-white font-bold text-sm">$</span></div>
                <div className="flex-1"><p className="font-medium text-slate-900 dark:text-white">Cash App</p><p className="text-xs text-slate-500">$Starrsie737</p></div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
            </div>
            <p className="text-xs text-slate-400 text-center mt-4">Thank you for supporting indie development! 🧵✨</p>
          </div>
        </div>
      )}

      {/* Global Stats Bar */}
      <ZennaStatsBar />

      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-zenna flex items-center justify-center shadow-lg shadow-zenna-violet/20"><span className="text-white font-bold text-lg sm:text-xl">Z</span></div>
            <span className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white">Zenna</span>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <ThemeToggle />
            <button onClick={() => setShowPreferences(true)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="My Preferences">
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </button>
            <button onClick={() => navigate('/marketplace')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="Marketplace">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
              Marketplace
            </button>
            <button onClick={() => navigate('/gallery')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="Community Gallery">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Gallery
            </button>
            <button onClick={() => setShowCraftMenu(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="Browse all crafts">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h10" /></svg>
              Crafts
            </button>
            <button onClick={() => setShowWhatsNew(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-zenna-violet hover:bg-zenna-violet/10 transition-colors" title="What's New">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
              What's New
            </button>
            {/* Admin link - only visible to Starr */}
            {user?.email === 'starrcustomclothing@gmail.com' && (
              <button onClick={() => navigate('/admin')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Admin Dashboard">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                Admin
              </button>
            )}
            <div className="flex items-center gap-3">
              {user?.user_metadata?.avatar_url ? <img src={user.user_metadata.avatar_url} alt={user.user_metadata.full_name || 'User'} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-zenna-violet text-white flex items-center justify-center text-sm font-medium">{user?.email?.[0]?.toUpperCase() || 'U'}</div>}
              <span className="text-sm text-slate-600 dark:text-slate-400 hidden md:block">{user?.user_metadata?.full_name || user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} loading={loading}>Sign Out</Button>
            </div>
          </div>
          <div className="flex sm:hidden items-center gap-2">
            <ThemeToggle />
            <button onClick={() => setShowPreferences(true)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"><svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></button>
            {/* Mobile admin link */}
            {user?.email === 'starrcustomclothing@gmail.com' && (
              <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </button>
            )}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">{mobileMenuOpen ? <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> : <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>}</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100 dark:border-slate-700">
              {user?.user_metadata?.avatar_url ? <img src={user.user_metadata.avatar_url} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-zenna-violet text-white flex items-center justify-center font-medium">{user?.email?.[0]?.toUpperCase() || 'U'}</div>}
              <div><p className="font-medium text-slate-900 dark:text-white text-sm">{user?.user_metadata?.full_name || 'User'}</p><p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{user?.email}</p></div>
            </div>
            <Button variant="secondary" className="w-full" onClick={handleLogout} loading={loading}>Sign Out</Button>
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
              <button onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false) }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700">🛒 Marketplace</button>
              <button onClick={() => { navigate('/gallery'); setMobileMenuOpen(false) }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700">🎨 Gallery</button>
              <button onClick={() => { setShowCraftMenu(true); setMobileMenuOpen(false) }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700">🧵 Crafts</button>
              <button onClick={() => { setShowWhatsNew(true); setMobileMenuOpen(false) }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-zenna-violet bg-zenna-violet/10 hover:bg-zenna-violet/20">✨ What's New</button>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex gap-6 flex-1">
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 sticky top-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Folders</h3>
                <button onClick={() => setShowNewFolderInput(true)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" title="New Folder"><svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></button>
              </div>
              {showNewFolderInput && (
                <div className="mb-2 flex gap-2">
                  <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && createFolder()} placeholder="Folder name..." className="flex-1 px-2 py-1 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white" autoFocus />
                  <button onClick={createFolder} className="p-1 text-zenna-emerald hover:bg-zenna-emerald/10 rounded"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                  <button onClick={() => { setShowNewFolderInput(false); setNewFolderName('') }} className="p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                </div>
              )}
              <div className="space-y-1">
                <button onClick={() => setSelectedFolder('all')} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selectedFolder === 'all' ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>All Projects<span className="ml-auto text-xs text-slate-400">{projects.length}</span></button>
                <button onClick={() => setSelectedFolder(null)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selectedFolder === null ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>Unfiled<span className="ml-auto text-xs text-slate-400">{projects.filter(p => !p.folder_id).length}</span></button>
                {folders.map(folder => (
                  <div key={folder.id} className="group relative">
                    {editingFolderId === folder.id ? (
                      <div className="flex gap-1 px-1">
                        <input type="text" value={editingFolderName} onChange={(e) => setEditingFolderName(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && renameFolder(folder.id)} className="flex-1 px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700" autoFocus />
                        <button onClick={() => renameFolder(folder.id)} className="p-1 text-zenna-emerald"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                        <button onClick={() => { setEditingFolderId(null); setEditingFolderName('') }} className="p-1 text-slate-400"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                      </div>
                    ) : (
                      <button onClick={() => setSelectedFolder(folder.id)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selectedFolder === folder.id ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg><span className="truncate">{folder.name}</span><span className="ml-auto text-xs text-slate-400">{projects.filter(p => p.folder_id === folder.id).length}</span></button>
                    )}
                    {editingFolderId !== folder.id && (
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditingFolderName(folder.name) }} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded" title="Rename"><svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                        <button onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id) }} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded" title="Delete"><svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-200 dark:border-slate-700 my-4"></div>
            <div className="space-y-2">
              <button onClick={() => setShowFeedback(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>Send Feedback</button>
              <button onClick={() => setShowDonate(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-gradient-to-r from-zenna-violet/10 to-zenna-cyan/10 text-zenna-violet hover:from-zenna-violet/20 hover:to-zenna-cyan/20 transition-colors"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>Support Zenna 💜</button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white">Welcome back{user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name.split(' ')[0]}` : ''}! 👋</h1>
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
                What would you like to create today?
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="ml-2 text-xs text-zenna-violet hover:underline"
                >
                  View tour
                </button>
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {userCredits.plan === 'free' && (
                <Button onClick={() => navigate('/pricing')} className="flex-1 sm:flex-none">View Plans</Button>
              )}
              <Button onClick={() => navigate('/workspace')} className="flex-1 sm:flex-none">+ New Project</Button>
            </div>
          </div>

          {/* Combined Stats + Crafting Journey */}
          <div className="bg-gradient-to-br from-zenna-violet/5 via-transparent to-zenna-cyan/5 border border-zenna-violet/20 rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] sm:text-xs font-bold text-zenna-violet uppercase tracking-wider">✨ Your Crafting Journey</div>
              </div>
              {userCredits.plan === 'free' && (
                <button onClick={() => navigate('/pricing')} className="text-xs text-zenna-violet hover:underline">Upgrade →</button>
              )}
            </div>

            {/* Row 1: Account stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] text-slate-500 uppercase font-semibold">Credits</div>
                  <button onClick={() => navigate('/pricing')} className="text-[9px] text-zenna-violet hover:underline">+ Buy</button>
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">{stats.credits.remaining}<span className="text-slate-400 text-xs font-normal"> left</span></div>
                <div className="mt-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-zenna rounded-full transition-all duration-500" style={{ width: `${Math.min((stats.credits.used / stats.credits.limit) * 100, 100)}%` }} />
                </div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Plan</div>
                <div className={`text-lg font-bold ${userCredits.plan === 'founding' ? 'text-yellow-500' : 'text-slate-900 dark:text-white'}`}>{planNames[userCredits.plan] || 'Free'}</div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Projects</div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">{stats.projects}</div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">This Month</div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">{stats.thisMonth}</div>
              </div>
            </div>

            {/* Row 2: Crafting story stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Favorite Craft</div>
                <div className="text-sm font-bold text-zenna-violet capitalize truncate" title={analytics?.favoriteCraft || 'exploring'}>
                  {analytics?.favoriteCraft ? analytics.favoriteCraft.replace(/-/g, ' ') : 'Exploring!'}
                </div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Total Designs</div>
                <div className="text-sm font-bold text-zenna-cyan">{analytics?.totalDesigns || stats.projects}</div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Days Crafting</div>
                <div className="text-sm font-bold text-emerald-500">{analytics?.daysUsing || 0}</div>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 backdrop-blur-sm">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Active This Week</div>
                <div className="text-sm font-bold text-amber-500">{analytics?.weekDays || 0} {(analytics?.weekDays || 0) === 1 ? 'day' : 'days'}</div>
              </div>
            </div>
          </div>

          {/* Referral + Challenge moved below projects — see below */}

          {/* Credits reminder — compact, not full-width */}
          {userCredits.plan === 'free' && stats.credits.remaining <= 3 && (
            <div className="bg-gradient-zenna rounded-lg px-4 py-2 mb-4 text-white text-sm flex items-center justify-between">
              <span>You have {stats.credits.remaining} free credit{stats.credits.remaining !== 1 ? 's' : ''} remaining</span>
              <button onClick={() => navigate('/pricing')} className="text-xs font-medium bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition">Upgrade</button>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader><CardTitle className="text-lg sm:text-xl">Start Something New</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                    {[
                      { icon: '🧵', label: 'Cross-Stitch',    prompt: 'Create a cross-stitch pattern', craft: 'cross-stitch',   color: 'from-pink-500/10 to-purple-500/10 border-pink-400/30' },
                      { icon: '✂️',  label: 'Sewing',          prompt: 'I want to sew',                 craft: 'sewing',         color: 'from-blue-500/10 to-cyan-500/10 border-blue-400/30' },
                      { icon: '🟦', label: 'Perler Beads',    prompt: 'Create a perler bead pattern',  craft: 'perler-beads',   color: 'from-red-500/10 to-orange-500/10 border-red-400/30' },
                      { icon: '🎨', label: 'Cricut / Vinyl',  prompt: 'Create a vinyl decal design',   craft: 'vinyl-decal',    color: 'from-violet-500/10 to-fuchsia-500/10 border-violet-400/30' },
                      { icon: '🧩', label: 'Quilting',        prompt: 'Create a quilt block pattern',  craft: 'quilting',       color: 'from-emerald-500/10 to-teal-500/10 border-emerald-400/30' },
                      { icon: '🧶', label: 'Crochet',         prompt: 'Create a crochet pattern',      craft: 'crochet',        color: 'from-rose-500/10 to-pink-500/10 border-rose-400/30' },
                      { icon: '🖍️', label: 'Coloring Page',  prompt: 'Create an adult coloring page', craft: 'coloring-page',  color: 'from-yellow-500/10 to-amber-500/10 border-yellow-400/30' },
                      { icon: '👕', label: 'T-Shirt Design',  prompt: 'Design a t-shirt',              craft: 't-shirt-design', color: 'from-slate-500/10 to-slate-600/10 border-slate-400/30' },
                    ].map((card, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const params = new URLSearchParams({ startPrompt: card.prompt, startCraft: card.craft })
                          navigate(`/workspace?${params.toString()}`)
                        }}
                        className={`p-3 sm:p-4 rounded-xl bg-gradient-to-br border ${card.color} hover:scale-105 hover:shadow-lg text-center transition-all`}
                      >
                        <span className="text-2xl sm:text-3xl block mb-1">{card.icon}</span>
                        <span className="text-[11px] sm:text-xs font-semibold text-slate-700 dark:text-slate-200">{card.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Quick actions strip */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={() => navigate('/workspace')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zenna-violet/10 text-zenna-violet hover:bg-zenna-violet/20 transition">
                      ✨ Free chat with Zenna
                    </button>
                    <button onClick={() => navigate('/gallery')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition">
                      🎨 Browse gallery
                    </button>
                    <button onClick={() => setShowPreferences(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition">
                      ⚙️ Preferences
                    </button>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-400 ml-auto">
                      <kbd className="font-mono bg-white dark:bg-slate-800 px-1 rounded border border-slate-300 dark:border-slate-600 text-[10px]">⌘K</kbd>
                      Search
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card><CardHeader><CardTitle className="text-lg sm:text-xl">{selectedFolder === 'all' ? 'All Projects' : selectedFolder === null ? 'Unfiled Projects' : folders.find(f => f.id === selectedFolder)?.name || 'Projects'}</CardTitle></CardHeader><CardContent>
                {/* Search + Sort + Bulk toggle */}
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input
                      type="text"
                      value={projectSearch}
                      onChange={e => setProjectSearch(e.target.value)}
                      placeholder="Search projects..."
                      className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-zenna-violet"
                    />
                  </div>
                  <select
                    value={projectSort}
                    onChange={e => setProjectSort(e.target.value)}
                    className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                  >
                    <option value="updated">Recent</option>
                    <option value="created">Newest</option>
                    <option value="name">A-Z</option>
                  </select>
                  <button
                    onClick={() => { setBulkMode(!bulkMode); setSelectedProjectIds([]) }}
                    className={`text-xs px-2 py-1.5 rounded-lg border transition-colors ${bulkMode ? 'bg-zenna-violet text-white border-zenna-violet' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'}`}
                    title="Select multiple projects"
                  >
                    {bulkMode ? 'Cancel' : 'Select'}
                  </button>
                </div>

                {/* Bulk action bar */}
                {bulkMode && selectedProjectIds.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 p-2 bg-zenna-violet/10 border border-zenna-violet/30 rounded-lg">
                    <span className="text-xs font-medium text-zenna-violet">{selectedProjectIds.length} selected</span>
                    <select
                      onChange={e => { if (e.target.value !== '') { handleBulkMove(e.target.value === 'null' ? null : e.target.value); e.target.value = '' } }}
                      className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                      defaultValue=""
                    >
                      <option value="" disabled>Move to...</option>
                      <option value="null">Unfiled</option>
                      {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                    <button onClick={handleBulkDelete} className="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600 ml-auto">
                      Delete
                    </button>
                  </div>
                )}
              </CardContent><CardContent>
                {loadingProjects ? <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin"></div></div> : filteredProjects.length === 0 ? <div className="text-center py-8"><p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{selectedFolder === null ? 'No unfiled projects' : 'No projects in this folder'}</p><Button size="sm" onClick={() => navigate('/workspace')}>Create a project</Button></div> : (
                  <div className="space-y-2 sm:space-y-3">
                    {(showAllProjects ? filteredProjects : filteredProjects.slice(0, 5)).map((project) => {
                      const isSelected = selectedProjectIds.includes(project.id)
                      return (
                      <div
                        key={project.id}
                        className={`group w-full flex items-center gap-3 p-3 sm:p-3 rounded-lg transition-colors cursor-pointer min-h-[56px] ${isSelected ? 'bg-zenna-violet/10 border border-zenna-violet/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600'}`}
                        onClick={() => {
                          if (bulkMode) {
                            toggleProjectSelected(project.id)
                          } else {
                            navigate(`/workspace?project=${project.id}`)
                          }
                        }}
                      >
                        {bulkMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProjectSelected(project.id)}
                            onClick={e => e.stopPropagation()}
                            className="w-5 h-5 accent-zenna-violet shrink-0"
                          />
                        )}
                        {project.images?.[0] ? <img src={project.images[0]} alt={project.name} className="w-11 h-11 rounded-lg object-cover shrink-0" /> : <div className="w-11 h-11 rounded-lg bg-slate-100 dark:bg-slate-600 flex items-center justify-center text-xs font-mono uppercase text-slate-500 dark:text-slate-400 shrink-0">{getProjectType(project)}</div>}
                        <div className="flex-1 min-w-0"><p className="font-medium text-slate-900 dark:text-white truncate text-sm sm:text-base">{project.name}</p><p className="text-xs text-slate-500 dark:text-slate-400">{formatRelativeTime(project.updated_at)}</p></div>
                        {!bulkMode && (
                          <>
                            {/* Desktop: hover reveals move dropdown */}
                            <div className="hidden md:block relative opacity-0 group-hover:opacity-100 transition-all">
                              <select onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); moveProjectToFolder(project.id, e.target.value === 'null' ? null : e.target.value) }} value={project.folder_id || 'null'} className="text-xs p-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 cursor-pointer" title="Move to folder">
                                <option value="null">Unfiled</option>
                                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                              </select>
                            </div>
                            {/* Duplicate — always visible on mobile, hover on desktop */}
                            <button
                              onClick={(e) => handleDuplicateProject(project.id, e)}
                              className="p-2 rounded-lg hover:bg-zenna-cyan/10 active:bg-zenna-cyan/20 text-zenna-cyan transition-all md:opacity-0 md:group-hover:opacity-100 shrink-0 tap-target"
                              title="Duplicate project"
                              aria-label="Duplicate project"
                            >
                              <svg className="w-5 h-5 md:w-4 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            </button>
                            {/* Delete — always visible on mobile, hover on desktop */}
                            <button
                              onClick={(e) => handleDeleteProject(project.id, e)}
                              className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 active:bg-red-200 dark:active:bg-red-900/40 text-red-500 transition-all md:opacity-0 md:group-hover:opacity-100 shrink-0 tap-target"
                              title="Delete project"
                              aria-label="Delete project"
                            >
                              <svg className="w-5 h-5 md:w-4 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                      )
                    })}
                  </div>
                )}
                {filteredProjects.length > 5 && !showAllProjects && <button onClick={() => setShowAllProjects(true)} className="w-full mt-3 sm:mt-4 text-center text-xs sm:text-sm text-zenna-violet hover:underline">View all {filteredProjects.length} projects →</button>}
                {showAllProjects && filteredProjects.length > 5 && <button onClick={() => setShowAllProjects(false)} className="w-full mt-3 sm:mt-4 text-center text-xs sm:text-sm text-slate-400 hover:underline">Show less ↑</button>}
              </CardContent></Card>
            </div>
          </div>

          {/* Referral + Challenge — compact side-by-side below projects */}
          {(referralInfo || activeChallenge) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4 sm:mt-6">
              {referralInfo && (
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-400/30 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">💜</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Refer a friend</div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                        {referralInfo.redemptions > 0
                          ? `${referralInfo.total_bonus_earned} credits earned from ${referralInfo.redemptions} referral${referralInfo.redemptions > 1 ? 's' : ''}!`
                          : `Earn ${referralInfo.bonus_per_referral} credits when they upgrade`}
                      </p>
                    </div>
                    <button
                      onClick={copyReferralLink}
                      className="shrink-0 px-3 py-1.5 text-xs font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition"
                    >
                      {referralCopied ? '✓ Copied' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              )}
              {activeChallenge && (
                <div
                  onClick={() => navigate('/gallery')}
                  className="bg-gradient-to-r from-amber-500/10 to-pink-500/10 border border-amber-400/30 rounded-xl p-3 sm:p-4 cursor-pointer hover:border-amber-400/60 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{activeChallenge.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">🏆 Challenge</div>
                      <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">{activeChallenge.title}</p>
                    </div>
                    <span className="text-xs text-amber-600 dark:text-amber-400 shrink-0">Join →</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="lg:hidden flex gap-3 mt-6">
            <button onClick={() => setShowFeedback(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>Feedback</button>
            <button onClick={() => setShowDonate(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-zenna-violet/10 to-zenna-cyan/10 text-sm text-zenna-violet"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>Support 💜</button>
          </div>
        </main>
      </div>

      {/* Support Email CTA at bottom */}
      <SupportEmailCTA />
      
      {/* Safety Warning Banner */}
      <div className="bg-red-600 text-white text-center py-2 px-4 text-xs sm:text-sm">
        ⚠️ Zenna is designed for craft projects only. Artistic nudity for legitimate purposes is permitted, but suspicious requests for explicit or illegal content are logged and may result in account suspension and/or be reported to authorities.
      </div>

      {/* Footer Links */}
      <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400 bg-zenna-cream dark:bg-slate-900">
        <Link to="/privacy" className="hover:text-zenna-violet transition-colors">Privacy</Link>
        <span className="mx-2">•</span>
        <Link to="/terms" className="hover:text-zenna-violet transition-colors">Terms</Link>
      </div>
    </div>
  )
}

export default Dashboard